import { FiPlay } from "react-icons/fi";
import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './topbar.css';
import { AuthContext } from '../../context/AuthContext';
import { logoutCall } from '../../apiCalls';
import { axiosInstance } from '../../config';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import ProfilePictureModal from '../ProfilePictureModal/ProfilePictureModal';
import Avatar from '../Avatar/Avatar';

const PLATFORM_ICONS = {
  twitter: "𝕏",
  tiktok: "♪",
  youtube: <FiPlay />,
  facebook: "f",
};

const Topbar = ({ creditRemaining: creditRemainingProp, creditTotal: creditTotalProp, nextExpiringGrant, activeGrants, onUpgrade, userPlan: userPlanProp }) => {
  const { t } = useTranslation(['common', 'settings']);
  const { user: currentUser, dispatch } = useContext(AuthContext);
  const [user, setUser] = useState({});
  const [brands, setBrands] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState(null);
  const navigate = useNavigate();
  // If no onUpgrade handler is passed, fall back to navigating to /upgrade
  const handleUpgrade = onUpgrade || (() => navigate('/upgrade'));
  const location = useLocation();
  const PF = process.env.REACT_APP_PUBLIC_FOLDER || '/assets/';
  const [showDropdown, setShowDropdown] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [showCreditExpiry, setShowCreditExpiry] = useState(false);
  // Retry the avatar image whenever the active brand / brand list changes.
  useEffect(() => {
    setAvatarFailed(false);
  }, [activeBrandId, brands]);
  const dropdownRef = useRef(null);
  const [brandPicModal, setBrandPicModal] = useState({ open: false, id: null, name: '' });

  // Self-fetched credits/plan — used when parent doesn't pass props (e.g. MePage, CalendarPage)
  const [selfCreditsRemaining, setSelfCreditsRemaining] = useState(null);
  const [selfCreditsTotal, setSelfCreditsTotal] = useState(null);
  const [selfUserPlan, setSelfUserPlan] = useState(null);

  const needsSelfFetch = creditRemainingProp === undefined;

  // Resolved values — prefer props, fall back to self-fetched
  const creditRemaining = creditRemainingProp ?? selfCreditsRemaining;
  const creditTotal = creditTotalProp ?? selfCreditsTotal;
  const userPlan = userPlanProp ?? selfUserPlan;

  // Hide topbar on specific pages
  const shouldHideTopbar = () => {
    const hideTopbarRoutes = ['/profile', '/creator-dashboard', '/notifications'];
    return hideTopbarRoutes.includes(location.pathname);
  };

  const fetchUser = useCallback(async () => {
    try {
      if (!currentUser?._id) return;
      const res = await axiosInstance.get(`/users?userId=${currentUser._id}`);
      setUser(res.data || {});
    } catch (err) {
      console.error('Failed to fetch user:', err);
    }
  }, [currentUser]);

  const fetchBrands = useCallback(async () => {
    try {
      if (!currentUser?._id) return;
      const res = await axiosInstance.get('/users/me/brands');
      const fetchedBrands = res.data.brands || [];
      const fetchedActiveId = res.data.activeBrandId || null;
      setBrands(fetchedBrands);
      setActiveBrandId(fetchedActiveId);

      // Auto-prompt to upload brand avatar if missing — show every load until they upload
      const active = fetchedActiveId
        ? fetchedBrands.find(b => b._id === fetchedActiveId)
        : fetchedBrands[0];
      const isValidUrl = (url) => url && url.startsWith('http') && !url.includes('amazonaws.com');
      if (active && !isValidUrl(active.avatar)) {
        setBrandPicModal({ open: true, id: active._id, name: active.name });
      }
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchUser();
      fetchBrands();
    }
  }, [currentUser, fetchUser, fetchBrands]);

  // Fetch credits + plan when parent page doesn't provide them as props
  useEffect(() => {
    if (!needsSelfFetch || !currentUser?._id) return;
    axiosInstance.get('/billing/plan/status').then(res => {
      setSelfCreditsRemaining(res.data.generationsRemaining ?? null);
      setSelfCreditsTotal(res.data.creditsPeriodTotal ?? null);
      setSelfUserPlan(res.data.plan ?? null);
    }).catch(() => {});
  }, [needsSelfFetch, currentUser]);

  // Listen for profile picture updates — refetch brands to get updated avatar
  useEffect(() => {
    const handleUpdate = (e) => {
      if (e.detail?.profilePicture) {
        setUser(prev => ({ ...prev, profilePicture: e.detail.profilePicture }));
        fetchBrands();
      }
    };
    window.addEventListener("profile_picture_updated", handleUpdate);
    return () => window.removeEventListener("profile_picture_updated", handleUpdate);
  }, [fetchBrands]);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    const handleKey = (e) => e.key === "Escape" && setShowDropdown(false);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  const handleLogout = () => {
    logoutCall(dispatch);
    localStorage.removeItem("clipbait_token");
    navigate('/');
  };

  const handleSwitchBrand = async (brandId) => {
    try {
      await axiosInstance.put(`/users/me/brands/${brandId}/activate`);
      setActiveBrandId(brandId);
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch brand:', err);
    }
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleDeleteBrand = async (brandId) => {
    try {
      await axiosInstance.delete(`/users/me/brands/${brandId}`);
      setConfirmDeleteId(null);
      fetchBrands();
      if (brandId === activeBrandId) {
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to delete brand:', err);
    }
  };

  const handleAddBrand = () => {
    setShowDropdown(false);
    // Add Brand requires a subscription — a free account gets one brand only.
    // Until they subscribe, it just opens the upgrade popup (never navigates).
    if (!isSubscribed) {
      handleUpgrade();
      return;
    }
    navigate("/me?newBrand=true");
  };

  const handleSettings = () => {
    setShowDropdown(false);
    navigate("/me?tab=settings");
  };

  if (shouldHideTopbar()) return null;

  const activeBrand = brands.find(b => b._id === activeBrandId) || brands[0];

  // Credit ring — depleting arc like Higgsfield.
  // creditTotal = creditsPeriodTotal (set when credits are granted/purchased).
  // Ring drains from 100% → 0% as credits are consumed.
  const hasCredits = typeof creditRemaining === "number";
  const ringPct = hasCredits && creditTotal > 0
    ? Math.max(0, Math.min(100, Math.round((creditRemaining / creditTotal) * 100)))
    : hasCredits ? 100 : null;
  const ringColor = ringPct === null ? 'rgba(255,255,255,0.2)'
    : ringPct > 30 ? '#ff6a00'
    : ringPct > 10 ? '#f59e0b'
    : '#ef4444';
  const ringStyle = ringPct !== null
    ? { background: `conic-gradient(${ringColor} ${ringPct}%, rgba(255,255,255,0.1) ${ringPct}%)` }
    : {};

  const isFreeTrial = !userPlan || userPlan === "none";
  // A free account can run on credits with ONE brand. Adding more brands
  // requires an active subscription (credits alone don't unlock it).
  const isSubscribed = !!userPlan && userPlan !== "none";

  // Accept any real image URL (Google Cloud / CDN / data / blob). The old code
  // rejected amazonaws.com URLs; the real problem was that brands without their
  // own avatar showed an empty camera icon. A genuinely broken URL is caught by
  // onError below.
  const isValidProfilePic = (url) =>
    !!url && /^(https?:|data:|blob:)/.test(url);
  // Prefer the brand's own avatar; otherwise fall back to the user's (Google)
  // profile photo, then to a clean initial.
  const rawProfileImg = isValidProfilePic(activeBrand?.avatar)
    ? activeBrand.avatar
    : isValidProfilePic(currentUser?.profilePicture)
      ? currentUser.profilePicture
      : null;
  const profileImg = avatarFailed ? null : rawProfileImg;
  const needsProfilePicPrompt = !profileImg;
  // Clean initial fallback (like the reference SaaS avatar) instead of a camera.
  const avatarInitial = (
    activeBrand?.name ||
    currentUser?.username ||
    currentUser?.email ||
    "?"
  )
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <>
    <div className="topbar">
      <div className="topbar-left">
        <Link to="/" className="topbar-logo">CLIPBAIT</Link>
      </div>

      <div className="topbar-right">
        <LanguageSwitcher variant="desktop" />
        {currentUser && (
          <>
            {/* Upgrade button — always visible when on free trial, or as subtle link otherwise */}
            <button className={`topbar-upgrade-btn${isFreeTrial ? '' : ' topbar-upgrade-btn--subtle'}`} onClick={handleUpgrade}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
              {t('common:upgrade')}
            </button>
          </>
        )}
        {currentUser ? (
          <div className="profile-container" ref={dropdownRef}>
            <div className="topbar-brand-trigger" data-paywall-allow onClick={() => setShowDropdown(prev => !prev)}>
              <div className="topbar-credit-ring" style={ringStyle}>
                {profileImg ? (
                  <img
                    src={profileImg}
                    alt="profile"
                    className="topbar-profile-img"
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  <div className="topbar-profile-img topbar-profile-placeholder topbar-profile-initial">
                    {avatarInitial}
                  </div>
                )}
                {typeof creditRemaining === "number" && creditRemaining === 0 && (
                  <span className="topbar-credit-badge">0</span>
                )}
              </div>
              {activeBrand && (
                <span className="topbar-brand-name">{activeBrand.name}</span>
              )}
              {(user.referralSource || '').toLowerCase() === 'own' && (
                <span className="topbar-own-badge" title="OWN partner member">OWN</span>
              )}
              <span className="topbar-chevron">{showDropdown ? '▲' : '▼'}</span>
            </div>

            {showDropdown && (
              <div className="dropdown-menu-custom brand-dropdown">
                {/* User / Brand header */}
                <div className="dropdown-user-header">
                  <div className="dropdown-user-avatar">
                    <Avatar
                      src={activeBrand?.avatar}
                      name={activeBrand?.name || user.username || "C"}
                    />
                  </div>
                  <div className="dropdown-user-info">
                    <span className="dropdown-user-name">{activeBrand?.name || user.username || 'User'}</span>
                    <span className={`dropdown-user-plan${isFreeTrial ? ' dropdown-user-plan--free' : ''}`}>
                      {isFreeTrial ? t('common:freePlan') : t('common:planLabel', { plan: userPlan ? userPlan.charAt(0).toUpperCase() + userPlan.slice(1) : 'Pro' })}
                    </span>
                  </div>
                </div>

                {/* Update profile picture prompt */}
                {needsProfilePicPrompt && (
                  <>
                    <button
                      type="button"
                      className="dropdown-item-custom dropdown-update-pic-prompt"
                      onClick={() => { setShowDropdown(false); navigate('/me'); }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                      </svg>
                      <span>Update profile picture</span>
                      <span className="dropdown-topup-buy-badge" style={{ background: '#ff6a00' }}>!</span>
                    </button>
                    <div className="dropdown-divider" />
                  </>
                )}

                {/* Add Workspace */}
                <button
                  type="button"
                  className="dropdown-item-custom dropdown-add-brand"
                  onClick={handleAddBrand}
                >
                  <span className="brand-add-icon">+</span>
                  {t('settings:addBrand')}
                </button>

                <div className="dropdown-divider" />

                {/* Credits section */}
                {typeof creditRemaining === 'number' && (
                  <>
                    <div
                      className={`dropdown-credits-status${creditRemaining === 0 ? ' dropdown-credits-status--empty' : ''}`}
                      onClick={() => {
                        // Out of credits → upgrade. Otherwise toggle the
                        // per-grant expiry breakdown (collapsed by default so a
                        // long list doesn't bury Sign out on mobile).
                        if (creditRemaining === 0) {
                          setShowDropdown(false);
                          handleUpgrade();
                        } else {
                          setShowCreditExpiry((v) => !v);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <span>
                        {creditRemaining === 0
                          ? t('common:outOfCredits')
                          : t('common:creditsRemaining', { count: creditRemaining })}
                      </span>
                      <span className={`dropdown-credits-chevron${showCreditExpiry ? ' open' : ''}`}>›</span>
                    </div>
                    <div className="dropdown-credits-track">
                      <div
                        className="dropdown-credits-fill"
                        style={{ width: `${ringPct ?? 0}%`, background: ringColor }}
                      />
                    </div>
                    {/* Per-bucket expiration breakdown — hidden until the user
                        taps the credits row (keeps the menu short on mobile). */}
                    {showCreditExpiry && Array.isArray(activeGrants) && activeGrants.length > 0 && (
                      <div className="dropdown-credits-expiry">
                        {activeGrants.map((g, idx) => (
                          <div key={idx} className="dropdown-credits-expiry-row">
                            <span className="dropdown-credits-expiry-amount">{g.amount}</span>
                            <span className="dropdown-credits-expiry-meta">
                              {g.expiresAt
                                ? `Expires in ${g.daysRemaining} day${g.daysRemaining === 1 ? '' : 's'}`
                                : 'Never expires'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className="dropdown-item-custom dropdown-topup-row"
                      onClick={() => { setShowDropdown(false); navigate('/upgrade?tab=credits'); }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                      </svg>
                      <span style={{ flex: 1 }}>{t('common:topUpCredits')}</span>
                      <span className="dropdown-topup-buy-badge">{t('common:buy')}</span>
                    </button>
                    <div className="dropdown-divider" />
                  </>
                )}

                {/* Brand List — scrollable so unlimited brands (admin) don't
                    overflow the viewport on mobile. */}
                <div className="brand-list-scroll">
                {brands.map((brand) => {
                  const isActive = activeBrandId && brand._id === activeBrandId;
                  return (
                    <button
                      key={brand._id}
                      type="button"
                      className={`dropdown-item-custom brand-item ${isActive ? 'brand-active' : ''}`}
                      onClick={() => !isActive && handleSwitchBrand(brand._id)}
                    >
                      <div className="brand-item-left">
                        <div className={`brand-avatar ${isActive ? 'brand-avatar-active' : ''}`}>
                          {isValidProfilePic(brand.avatar) ? (
                            <Avatar src={brand.avatar} name={brand.name} />
                          ) : (
                            <span
                              className="brand-avatar-add-photo"
                              title="Add profile picture"
                              onClick={(e) => { e.stopPropagation(); setShowDropdown(false); setBrandPicModal({ open: true, id: brand._id, name: brand.name }); }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <circle cx="12" cy="13" r="4"/>
                              </svg>
                            </span>
                          )}
                        </div>
                        <div className="brand-info">
                          <span className="brand-name">{brand.name}</span>
                          {brand.connectedPlatforms && brand.connectedPlatforms.length > 0 && (
                            <span className="brand-platforms">
                              {brand.connectedPlatforms.map((p) => (
                                <span key={p} className="brand-platform-icon">
                                  {PLATFORM_ICONS[p] || p}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                      </div>
                      {confirmDeleteId === brand._id ? (
                        <div className="brand-delete-confirm" onClick={(e) => e.stopPropagation()}>
                          <button className="brand-delete-yes" onClick={(e) => { e.stopPropagation(); handleDeleteBrand(brand._id); }}>
                            {t('common:delete')}
                          </button>
                          <button className="brand-delete-no" onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}>
                            {t('common:cancel')}
                          </button>
                        </div>
                      ) : brands.length > 1 && (
                        <span
                          className="brand-delete-icon"
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(brand._id); }}
                          title={t('common:delete')}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </span>
                      )}
                    </button>
                  );
                })}
                </div>

                <div className="dropdown-divider" />

                {/* Nav actions */}
                <button
                  type="button"
                  className="dropdown-item-custom dropdown-nav-item"
                  onClick={handleSettings}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                  {t('common:manageAccount')}
                </button>

                <div className="dropdown-divider" />

                <button
                  type="button"
                  data-paywall-allow
                  onClick={handleLogout}
                  className="dropdown-item-custom dropdown-logout"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  {t('common:signOut')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <a
              href={`https://app.clipbait.ai/login`}
              className="topbar-btn"
            >
              {t('login')}
            </a>

            <a
              href={`https://app.clipbait.ai/signup`}
              className="topbar-btn"
            >
              {t('signUp')}
            </a>
          </>
        )}
      </div>
    </div>

    {/* Brand avatar upload modal */}
    <ProfilePictureModal
      isOpen={brandPicModal.open}
      brandId={brandPicModal.id}
      brandName={brandPicModal.name}
      onClose={() => setBrandPicModal({ open: false, id: null, name: '' })}
      onUploaded={(newUrl, bId) => {
        setBrands(prev => prev.map(b => b._id === bId ? { ...b, avatar: newUrl } : b));
        setBrandPicModal({ open: false, id: null, name: '' });
      }}
    />
    </>
  );
};

export default Topbar;
